import { useEffect, useRef } from 'react';
import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';

interface PlannerDashboardTourProps {
  shouldRun: boolean;
}

const SEEN_KEY = 'hasSeenDashboardWalkthrough';
const SCROLL_CONTAINER_ID = 'main-content';

// Matches Navigation.tsx DESKTOP_QUERY
const isMobile = () => window.matchMedia('(max-width: 1023px)').matches;

/**
 * When multiple elements match a selector (e.g. desktop + mobile RoleSwitcher),
 * return the one that is actually visible (non-zero layout size).
 */
function findVisibleElement(selector: string): Element | string {
  const all = document.querySelectorAll(selector);
  for (const el of all) {
    if ((el as HTMLElement).offsetWidth > 0) return el;
  }
  return selector; // fallback to string if none visible
}

// CSS injected into <head> while the tour is mounted to style Driver.js elements
// to match the app's design system (font-sans, cyan-600 buttons, rounded popover)
const TOUR_STYLES = `
  /* Popover container */
  .driver-popover {
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
      "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif !important;
    border-radius: 12px !important;
    border: 2px solid #0891b2 !important;
    padding: 20px 20px 16px !important;
    box-shadow: 0 12px 28px -4px rgba(0,0,0,0.18), 0 6px 14px -6px rgba(0,0,0,0.12) !important;
    max-width: 320px !important;
  }

  /* Title */
  .driver-popover-title {
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
      "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
    font-size: 15px !important;
    font-weight: 600 !important;
    color: #1c1917 !important;
    margin-bottom: 6px !important;
    line-height: 1.4 !important;
  }

  /* Body copy */
  .driver-popover-description {
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
      "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
    font-size: 13px !important;
    color: #57534e !important;
    line-height: 1.55 !important;
  }

  /* Footer / button row */
  .driver-popover-footer {
    margin-top: 16px !important;
    gap: 8px !important;
    align-items: center !important;
  }

  /* Step progress text  e.g. "1 of 6" */
  .driver-popover-progress-text {
    font-family: ui-sans-serif, system-ui, sans-serif !important;
    font-size: 12px !important;
    color: #a8a29e !important;
  }

  /* Previous button — outlined style */
  .driver-popover-prev-btn {
    font-family: ui-sans-serif, system-ui, sans-serif !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    color: #0891b2 !important;
    background: transparent !important;
    border: 1px solid #0891b2 !important;
    border-radius: 6px !important;
    padding: 6px 14px !important;
    cursor: pointer !important;
    text-shadow: none !important;
  }
  .driver-popover-prev-btn:hover {
    background: #ecfeff !important;
  }
  .driver-popover-prev-btn:focus-visible {
    outline: 2px solid #0891b2 !important;
    outline-offset: 2px !important;
  }

  /* Next / Finish button — filled cyan-600 */
  .driver-popover-next-btn {
    font-family: ui-sans-serif, system-ui, sans-serif !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    color: #fff !important;
    background: #0891b2 !important;
    border: 1px solid #0891b2 !important;
    border-radius: 6px !important;
    padding: 6px 14px !important;
    cursor: pointer !important;
    text-shadow: none !important;
  }
  .driver-popover-next-btn:hover {
    background: #0e7490 !important;
    border-color: #0e7490 !important;
  }
  .driver-popover-next-btn:focus-visible {
    outline: 2px solid #0891b2 !important;
    outline-offset: 2px !important;
  }

  /* Close (×) button */
  .driver-popover-close-btn {
    color: #78716c !important;
  }
  .driver-popover-close-btn:hover {
    color: #1c1917 !important;
  }

  /* Arrow / caret inherits popover bg — no override needed */
`;

const getSteps = (): DriveStep[] => {
  const mobile = isMobile();

  return [
    {
      element: '[data-tour="planner-dashboard"]',
      popover: {
        title: 'Your Estate Plan Dashboard',
        description:
          'This is your estate planning dashboard. Here you can track your progress and see the next steps to complete your plan.',
        side: 'over',
        align: 'start',
      },
    },
    {
      element: mobile
        ? '[data-tour="planner-mobile-nav-trigger"]'
        : '[data-tour="planner-left-nav"]',
      popover: {
        title: 'Navigate Your Plan',
        description:
          'Use the menu to move between your plan, records, and shared access. Everything in your estate plan can be accessed from here. At the bottom, you can also access your account settings and support.',
        side: mobile ? 'bottom' : 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="planner-progress"]',
      popover: {
        title: 'Track Your Progress',
        description:
          'Your overall progress shows how many planning steps you\'ve completed. As you move through each section, this will update to reflect your progress across your entire plan.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="planner-workflow-tiles"]',
      popover: {
        title: 'Complete Planning Steps',
        description:
          'Each section represents a part of your estate plan. You can start, continue, or review any section at any time. Completed sections allow you to review or make updates as needed.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: '[data-tour="planner-plan-access"]',
      popover: {
        title: 'Share Your Plan',
        description:
          'Invite your executor or trusted individuals to access your plan when needed. You stay in control of who can view or manage your information.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: findVisibleElement('[data-tour="planner-role-switcher"]'),
      popover: {
        title: 'Profile and Roles',
        description:
          'If you have multiple roles, such as Planner, Executor, or Beneficiary, you can switch between dashboards at any time.',
        side: 'bottom',
        align: 'end',
      },
    },
  ];
};

/** Restore scroll on the main content container */
function restoreScroll() {
  const main = document.getElementById(SCROLL_CONTAINER_ID);
  if (main) main.style.overflow = '';
}

export default function PlannerDashboardTour({ shouldRun }: PlannerDashboardTourProps) {
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);

  // Inject app-matched CSS for the Driver.js popover while the tour component is mounted
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.id = 'planner-tour-styles';
    styleEl.textContent = TOUR_STYLES;
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
  }, []);

  useEffect(() => {
    if (!shouldRun) return;

    let timerId: ReturnType<typeof setTimeout>;

    const rafId = requestAnimationFrame(() => {
      timerId = setTimeout(() => {
        // Filter out steps whose target element is not in the DOM or not visible
        const steps = getSteps().filter((step) => {
          if (!step.element) return true;
          if (step.element instanceof Element) return (step.element as HTMLElement).offsetWidth > 0;
          return document.querySelector(step.element) !== null;
        });

        if (steps.length === 0) {
          localStorage.setItem(SEEN_KEY, 'true');
          return;
        }

        const driverInstance = driver({
          showProgress: true,
          animate: true,
          overlayOpacity: 0.6,
          allowClose: true,
          stagePadding: 6,
          stageRadius: 8,
          disableActiveInteraction: true,
          nextBtnText: 'Next',
          prevBtnText: 'Previous',
          doneBtnText: 'Finish',
          steps,
          onHighlighted: (element) => {
            // Lock scroll when Step 4 (workflow tiles) is active
            if (element?.getAttribute?.('data-tour') === 'planner-workflow-tiles') {
              const main = document.getElementById(SCROLL_CONTAINER_ID);
              if (main) main.style.overflow = 'hidden';
            }
          },
          onDeselected: (element) => {
            // Unlock scroll when leaving Step 4
            if (element?.getAttribute?.('data-tour') === 'planner-workflow-tiles') {
              restoreScroll();
            }
          },
          onDestroyed: () => {
            localStorage.setItem(SEEN_KEY, 'true');
            // Safety net: restore scroll in case tour ends during Step 4
            restoreScroll();
          },
        });

        driverRef.current = driverInstance;
        driverInstance.drive();
      }, 150);
    });

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timerId);
      driverRef.current?.destroy();
      driverRef.current = null;
      // Restore scroll if component unmounts during tour
      restoreScroll();
    };
  }, [shouldRun]);

  return null;
}
